import { useEffect, useRef, useState } from 'react';
import { formatDuration } from '@srcbattle/shared';
import type { EntryView } from '../api.ts';
import { Button } from './ui.tsx';
import { YouTubePlayer, type YouTubePlayerHandle } from './YouTubePlayer.tsx';
import { SpotifyPlayer } from './SpotifyPlayer.tsx';

/**
 * Choosing which part of your entry the room hears.
 *
 * Two things shape this, and both came from the first real game night.
 *
 * **It has to be optional.** The previous version put a slider on every entry
 * pre-set to a heuristic guess, which quietly decided for people who had not
 * decided. Now an entry starts at the beginning and the picker is a thing you
 * open when you have an opinion.
 *
 * **It cannot need the server to know the length.** Without a YouTube API key
 * every entry has a duration of 0 — which is exactly the setup this shipped to,
 * and it made the feature invisible. So the picker loads the real thing, reads
 * the length from the player, and sends it back. That also fills in the
 * "length unknown" the entry was showing.
 *
 * Scrub in the player or drag the slider; "Start here" takes wherever the
 * player currently is, which is the natural move once you have found the bit.
 */
/** `1:18`, `78`, or `1:02:03`. Returns null for anything else. */
function parseTimestamp(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const parts = trimmed.split(':').map((p) => Number(p));
  if (parts.some((p) => !Number.isFinite(p) || p < 0)) return null;

  const seconds = parts.reduce((total, part) => total * 60 + part, 0);
  return Number.isFinite(seconds) ? Math.round(seconds * 1000) : null;
}

export function ClipPicker({
  entry,
  onSave,
  onClose,
}: {
  entry: EntryView;
  onSave: (startMs: number, durationMs: number) => Promise<void> | void;
  onClose: () => void;
}) {
  const playerRef = useRef<YouTubePlayerHandle>(null);
  const [value, setValue] = useState(entry.clipStartMs);
  const [duration, setDuration] = useState(entry.durationMs);
  const [saving, setSaving] = useState(false);
  const [previewOnly, setPreviewOnly] = useState(false);

  const isSpotify = entry.source === 'spotify' && entry.spotifyTrackId;

  /**
   * One value, two ways to move it.
   *
   * Dragging the slider seeks the player; playing the player moves the slider.
   * "Start here" then just takes that value, which is what makes scrubbing in
   * the embed and dragging the handle feel like the same control rather than
   * two settings that can disagree.
   *
   * Movement is detected by the position changing between polls rather than by
   * asking whether it is playing — neither provider offers that cheaply, and a
   * position that is advancing is the same fact.
   */
  const lastSeenRef = useRef(0);
  useEffect(() => {
    const timer = setInterval(() => {
      const player = playerRef.current;
      if (!player) return;

      // A player that has not loaded reports a length of zero *and* a position
      // of zero. Without this guard that zero looks like a seek to the start,
      // and the handle snaps back to 0:00 the moment after it is dragged.
      const reported = player.getDurationMs();
      if (reported <= 0) return;
      if (duration <= 0) setDuration(reported);

      const at = player.getPositionMs();
      if (at !== lastSeenRef.current) {
        lastSeenRef.current = at;
        setValue(at);
      }
    }, 300);
    return () => clearInterval(timer);
  }, [duration]);

  // Keep a little room at the end: a clip that starts ten seconds before the
  // outro is not a clip.
  const maxStart = duration > 0 ? Math.max(0, duration - Math.min(entry.clipLengthMs, duration)) : 0;
  const clipEnd = duration > 0 ? Math.min(duration, value + entry.clipLengthMs) : value + entry.clipLengthMs;

  const save = async (startMs: number) => {
    setSaving(true);
    try {
      await onSave(startMs, duration);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 rounded-lg border border-border bg-surface p-3">
      <div className="mb-2">
        {isSpotify ? (
          <SpotifyPlayer
            ref={playerRef}
            trackId={entry.spotifyTrackId as string}
            startSeconds={entry.clipStartMs / 1000}
            shouldPlay={false}
            awaitingStart={false}
            onPreviewDetected={setPreviewOnly}
          />
        ) : (
          <YouTubePlayer
            ref={playerRef}
            videoId={entry.youtubeVideoId ?? ''}
            startSeconds={entry.clipStartMs / 1000}
            shouldPlay={false}
            targetPositionMs={null}
            awaitingStart={false}
          />
        )}
      </div>

      {previewOnly && (
        <div className="mb-2 rounded-lg border border-warning/40 bg-warning/10 px-2.5 py-1.5 text-xs">
          <p>
            <strong>Only the 30-second preview is playing.</strong> Sign in to Spotify in this
            browser, or update Widevine (<code>chrome://components</code>) and restart.
          </p>
          <p className="mt-1">
            You can still set the start point below. It applies to the full track.
          </p>
        </div>
      )}

      <p className="mb-1 flex items-baseline justify-between gap-2 text-xs">
        <span className="text-text-muted">
          {isSpotify
            ? 'Press ▶ on the player, find the bit you want, then press Start here.'
            : 'Play it, find the bit you want, then press Start here.'}
        </span>
        <span className="shrink-0 font-mono text-text-faint">
          {formatDuration(value)} – {formatDuration(clipEnd)}
        </span>
      </p>

      {maxStart > 0 ? (
        <input
          type="range"
          min={0}
          max={maxStart}
          step={1000}
          value={Math.min(value, maxStart)}
          aria-label="Clip start"
          onChange={(event) => {
            const next = Number(event.target.value);
            setValue(next);
            lastSeenRef.current = next;
            // Move the preview with the handle, so dragging is a scrub rather
            // than a guess you have to check afterwards.
            playerRef.current?.seekToMs(next);
          }}
          className="w-full accent-[var(--color-accent)]"
        />
      ) : (
        <p className="text-xs text-text-faint">
          Working out how long this one is — use <strong>Start here</strong> in the meantime.
        </p>
      )}

      {/* Typing the time is the way in when scrubbing is not: a Spotify preview,
          or a length nothing has reported yet. */}
      <label
        className={
          previewOnly
            ? 'mt-2 flex items-center gap-2 text-sm font-medium text-text'
            : 'mt-2 flex items-center gap-2 text-xs text-text-faint'
        }
      >
        {previewOnly ? 'Start at' : 'Or type it'}
        <input
          type="text"
          inputMode="numeric"
          placeholder="1:18"
          defaultValue={value > 0 ? formatDuration(value) : ''}
          onBlur={(event) => {
            const parsed = parseTimestamp(event.target.value);
            if (parsed === null) return;
            setValue(parsed);
            lastSeenRef.current = parsed;
            playerRef.current?.seekToMs(parsed);
          }}
          className="w-24 rounded border border-border-strong bg-surface px-2 py-1 font-mono text-text"
        />
      </label>

      <div className="mt-2 flex flex-wrap gap-2">
        <Button onClick={() => void save(value)} loading={saving}>
          Start here
        </Button>
        <Button variant="secondary" onClick={() => void save(0)} disabled={saving}>
          From the beginning
        </Button>
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
